import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const LoadingContext = createContext();

export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error('useLoading must be used within LoadingProvider');
    }
    return context;
};

// Check if cached settings exist
const hasCachedSettings = () => {
    try {
        const cached = localStorage.getItem('app_settings');
        return cached && cached !== '{}';
    } catch {
        return false;
    }
};

export const LoadingProvider = ({ children }) => {
    // Start with loading count of 1 ONLY if no cache exists (initial load)
    // If cache exists, skip loading screen for instant display
    const [loadingCount, setLoadingCount] = useState(hasCachedSettings() ? 0 : 1);
    const [hasLoaded, setHasLoaded] = useState(hasCachedSettings());
    const timeoutRef = useRef(null);

    const startLoading = useCallback(() => {
        // Always allow loading count to increment (for data fetching)
        setLoadingCount(prev => prev + 1);
    }, []);

    const stopLoading = useCallback(() => {
        setLoadingCount(prev => {
            const newCount = Math.max(0, prev - 1);
            // Mark as loaded when count reaches 0 for the first time
            if (newCount === 0 && !hasLoaded) {
                setHasLoaded(true);
            }
            return newCount;
        });
    }, [hasLoaded]);

    // Only show loading screen if hasn't loaded yet
    const isLoading = loadingCount > 0 && !hasLoaded;

    // Auto-stop initial loading after 2 seconds (only if no cache)
    useEffect(() => {
        if (!hasLoaded && !hasCachedSettings()) {
            timeoutRef.current = setTimeout(() => {
                console.log('Initial loading timeout - stopping loading');
                setLoadingCount(0);
                setHasLoaded(true);
            }, 2000); // 2 seconds

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [hasLoaded]);

    return (
        <LoadingContext.Provider value={{ isLoading, startLoading, stopLoading, loadingCount, hasLoaded }}>
            {children}
        </LoadingContext.Provider>
    );
};
